import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import Login from './components/Login';
import Configuration from './components/Configuration';
import Dashboard from './components/Dashboard';
import MobileDashboard from './components/MobileDashboard';
import MobileCommands from './components/MobileCommands';
import MqttConsole from './components/MqttConsole';
import ConnectionLogs from './components/ConnectionLogs';
import ClientsManagement from './components/ClientsManagement';
import DeviceHistory from './components/DeviceHistory';
import GlobalHistory from './components/GlobalHistory';
import api from './services/api';
import { shouldUseMobileInterface } from './utils/deviceDetection';
import './App.css';

// Página standalone del historial de un dispositivo (se abre en pestaña nueva vía /device-history/:deviceId)
function DeviceHistoryRoute({ user, viewPreferences }) {
  const { deviceId } = useParams();
  return (
    <DeviceHistory
      deviceId={deviceId}
      user={user}
      viewPreferences={viewPreferences}
      standalone
      onClose={() => window.close()}
    />
  );
}

// Página standalone del Historial Global (se abre en pestaña nueva vía /global-history)
function GlobalHistoryRoute({ user, viewPreferences }) {
  return (
    <GlobalHistory
      user={user}
      viewPreferences={viewPreferences}
      standalone
      onClose={() => window.close()}
    />
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(shouldUseMobileInterface());
  const [viewPreferences, setViewPreferences] = useState({});
  const [commandPreferences, setCommandPreferences] = useState({});

  useEffect(() => {
    checkAuth();
  }, []);

  // Cargar preferencias de visualización cuando el usuario se autentica
  useEffect(() => {
    if (user) {
      loadViewPreferences();
    }
  }, [user]);

  const loadViewPreferences = async () => {
    try {
      const response = await api.get('/api/users/me/view-preferences');
      setViewPreferences(response.data.viewPreferences || {});
      setCommandPreferences(response.data.commandPreferences || {});
    } catch (error) {
      console.error('Error loading view preferences:', error);
    }
  };

  const handleViewPreferencesChange = (newPrefs) => {
    // newPrefs ahora puede ser { viewPreferences, commandPreferences }
    if (newPrefs.viewPreferences !== undefined) {
      setViewPreferences(newPrefs.viewPreferences);
    }
    if (newPrefs.commandPreferences !== undefined) {
      setCommandPreferences(newPrefs.commandPreferences);
    }
  };

  // Detectar cambios en el tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(shouldUseMobileInterface());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        api.setAuthToken(token);
        const response = await api.verify();
        setUser(response.data.user);
      } catch (error) {
        console.error('Auth verification failed:', error);
        localStorage.removeItem('token');
      }
    }
    
    setLoading(false);
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    api.setAuthToken(token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    // Limpiar alertas persistidas para que no se filtren al próximo usuario
    // que inicie sesión en el mismo navegador (fuga cross-tenant de alertas resueltas)
    localStorage.removeItem('criticalAlertsResolved');
    localStorage.removeItem('criticalAlertsAcknowledged');
    api.setAuthToken(null);
    setUser(null);
    // Resetear la URL para que el próximo login arranque en el dashboard
    window.history.replaceState(null, '', '/');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando plataforma IoT...</p>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  // Si es dispositivo móvil, usar interfaz móvil simplificada
  if (isMobile) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/mobile" replace />} />
          <Route path="/mobile" element={
            <MobileDashboard
              user={user}
              viewPreferences={viewPreferences}
              commandPreferences={commandPreferences}
              onViewPreferencesChange={handleViewPreferencesChange}
            />
          } />
          <Route path="/mobile-commands" element={
            <MobileCommands
              user={user}
              commandPreferences={commandPreferences}
              viewPreferences={viewPreferences}
              onViewPreferencesChange={handleViewPreferencesChange}
            />
          } />
          <Route path="/mobile-logout" element={
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f8fafc'
            }}>
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                maxWidth: '300px',
                width: '100%'
              }}>
                <h2 style={{ marginBottom: '1.5rem' }}>Cerrar Sesión</h2>
                <p style={{ marginBottom: '1.5rem', color: '#6b7280' }}>
                  ¿Estás seguro que deseas cerrar sesión?
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={handleLogout}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Sí, cerrar sesión
                  </button>
                  <button
                    onClick={() => window.history.back()}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          } />
          {/* Redirigir todas las demás rutas al dashboard móvil */}
          <Route path="*" element={<Navigate to="/mobile" replace />} />
        </Routes>
      </Router>
    );
  }

  // Interfaz de escritorio normal
  return (
    <Router>
      <Layout user={user} onLogout={handleLogout} onViewPreferencesChange={handleViewPreferencesChange}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/alerts" element={<Dashboard user={user} showAlertsOnly={true} viewPreferences={viewPreferences} commandPreferences={commandPreferences} />} />
          <Route path="/dashboard" element={<Dashboard user={user} viewPreferences={viewPreferences} commandPreferences={commandPreferences} />} />
          <Route path="/configuration" element={
            (user.role === 'admin' || user.role === 'operator') ?
              <Configuration user={user} /> :
              <Navigate to="/dashboard" replace />
          } />
          <Route path="/mqtt-console" element={
            user.role === 'admin' ?
              <MqttConsole /> :
              <Navigate to="/dashboard" replace />
          } />
          <Route path="/connection-logs" element={
            user.role === 'admin' ?
              <ConnectionLogs user={user} /> :
              <Navigate to="/dashboard" replace />
          } />
          <Route path="/clients" element={
            user.role === 'admin' ?
              <ClientsManagement user={user} /> :
              <Navigate to="/dashboard" replace />
          } />
          <Route path="/device-history/:deviceId" element={
            <DeviceHistoryRoute user={user} viewPreferences={viewPreferences} />
          } />
          <Route path="/global-history" element={
            <GlobalHistoryRoute user={user} viewPreferences={viewPreferences} />
          } />
          {/* Redirigir rutas móviles a desktop si se accede desde PC */}
          <Route path="/mobile" element={<Navigate to="/dashboard" replace />} />
          <Route path="/mobile-logout" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;
